import moment from 'moment';
import { FieldState, FormState } from 'formstate';
import { isFinite, isNumber } from 'lodash';
import { InjectedIntl } from 'react-intl';

import { ZohoReportProfile } from '../../modules/dashboard/types';
import { ICoverStyle } from '../../modules/finance/commons/components/exportMenu/components/exportDialog/components/DialogContent';
import { DynamicField, DynamicLayout } from '../../modules/setup/definitions/formsAndFields/types/DynamicFieldType';
import { RTUGlobalField } from '../../modules/setup/definitions/rtuFormsAndFields/type/FieldType';
import { FieldProps } from '../types/FieldPropsType';
import { intl } from './Intl';

export const getFieldProps = (
  form: FormState<any>,
  key: string,
  disabled: boolean,
  defaultLabel: string,
  location?: string,
  noFill?: boolean,
  optionsField?: any
): FieldProps => {
  const optionsTemp = optionsField ? optionsField : {};
  return {
    disabled,
    inline: !!optionsTemp.inline,
    fill: !noFill,
    id: key,
    layer: {
      labelWidth: optionsTemp.labelWidth ? optionsTemp.labelWidth : false,
      inputWidth: optionsTemp.inputWidth ? optionsTemp.inputWidth : false,
      labelOrientation: optionsTemp.labelOrientation ? optionsTemp.labelOrientation : 'start'
    },
    fieldState: form.$[key],
    label: `${intl(`setup.locations.${location}.form.${key}`, defaultLabel || '')}:`
  };
};

export const getFieldPropsTracking = (
  form: FormState<any>,
  key: string,
  disabled: boolean,
  defaultLabel: string,
  location?: string,
  noFill?: boolean,
  optionsField?: any
): FieldProps => {
  const optionsTemp = optionsField ? optionsField : {};
  return {
    disabled,
    inline: !!optionsTemp.inline,
    fill: !noFill,
    id: key,
    layer: {
      labelWidth: optionsTemp.labelWidth ? optionsTemp.labelWidth : false,
      inputWidth: optionsTemp.inputWidth ? optionsTemp.inputWidth : false,
      labelOrientation: optionsTemp.labelOrientation ? optionsTemp.labelOrientation : 'start'
    },
    fieldState: form.$[key],
    label: `${intl(`dispatch.traking.${location}.${key}`, defaultLabel || '')}:`
  };
};

export const getFieldPropsSetup = (
  form: FormState<any>,
  key: string,
  disabled: boolean,
  defaultLabel: string,
  intlPath: string = 'app',
  noFill?: boolean,
  optionsField?: any
): FieldProps => {
  const optionsTemp = optionsField ? optionsField : {};
  return {
    disabled,
    inline: !!optionsTemp.inline,
    fill: !noFill,
    id: key,
    layer: {
      labelWidth: optionsTemp.labelWidth ? optionsTemp.labelWidth : false,
      inputWidth: optionsTemp.inputWidth ? optionsTemp.inputWidth : false,
      labelOrientation: optionsTemp.labelOrientation ? optionsTemp.labelOrientation : 'start'
    },
    fieldState: form.$[key],
    label: `${intl(`${intlPath}.${key}`, defaultLabel || '')}:`
  };
};

export const doubleSpacesValidation = (Intl?: InjectedIntl) => (value: any) => {
  return (
    value?.toString().includes('  ') &&
    intl('app.errors.form.spacesConsecutive', "This field can't contain two consecutive spaces ")
  );
};

export const emailValidation = (Intl?: InjectedIntl) => (value: any) => {
  const regExp = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;
  return (
    value &&
    !value.toString().match(regExp) &&
    intl('app.errors.form.authEmail', 'Email address is not valid. Please try again.')
  );
};

export const onlyLetters = (intl: InjectedIntl) => (value: any) => {
  const regExp = /^[a-zA-Z\s]*$/;
  return (
    value &&
    !value.toString().match(regExp) &&
    `${intl.formatMessage({
      id: `app.errors.form.onlyLettersAndSpace`,
      defaultMessage: `This field can only contain letters and space`
    })}`
  );
};

export const motivePasswordValidator = () => (value: any) => {
  const regExp = /^(?=.*\d)(?=.*[a-zA-Z])[a-zA-Z0-9]{3,}$/;
  return (
    value &&
    !value.toString().match(regExp) &&
    `${intl('app.errors.form.motivepassword', 'The password is not valid. Please try again.')}`
  );
};

export const startSpace = (intl: InjectedIntl) => (value: any) => {
  const regExp = /^[\s]*$/;
  return (
    value &&
    value.toString().match(regExp) &&
    `${intl.formatMessage({
      id: 'app.errors.form.dontStartWithSpace',
      defaultMessage: `This field cannot start with a space`
    })}`
  );
};

export const onlyLettersAnNumbers = (intlOld?: InjectedIntl) => (value: any) => {
  const regExp = /^[a-zA-Z0-9\s]*$/;
  return (
    value &&
    !value.toString().match(regExp) &&
    `${intl(`app.errors.form.onlyLettersNumbersAndSpace`, `This field can only contain letters, numbers and space`)}`
  );
};

export const onlyLettersNumbersSpecialAndNoSpace = (value: any) => {
  const regExp = /^[A-Za-z0-9!@#$%^&*()_+-=;':",.<>/?{}\\|\[\]]*$/;
  return (
    value &&
    !value.toString().match(regExp) &&
    `${intl(
      `app.errors.form.onlyLettersNumbersAndSpecialCharaceters`,
      `This field can only contain letters, numbers and special characters`
    )}`
  );
};

export const onlyLettersAnNumbersNoSpace = (Intl?: InjectedIntl) => (value: any) => {
  const regExp = /^[a-zA-Z0-9]*$/;
  return (
    value &&
    !value.toString().match(regExp) &&
    intl('app.errors.form.onlyLettersNumbers', 'This field can only contain letters and numbers')
  );
};

export const onlyLowerCaseLettersAnNumbersNoSpace = (value: any) => {
  const regExp = /^[a-z0-9]*$/;
  return (
    value &&
    !value.toString().match(regExp) &&
    intl('app.errors.form.onlyLowercaseLettersNumbers', 'This field can only contain lowercase letters and numbers')
  );
};

export const onlyLowerCaseLettersNoSpace = (Intl?: InjectedIntl) => (value: any) => {
  const regExp = /^[a-z]*$/;
  return (
    value &&
    !value.toString().match(regExp) &&
    intl('app.errors.form.onlyLowercaseLetters', 'This field can only contain lowercase letters')
  );
};

export const onlyUpperCaseLettersAnNumbers = (intlOld?: InjectedIntl) => (value: any) => {
  const regExp = /^[A-Z0-9\s]*$/;
  return (
    value &&
    !value.toString().match(regExp) &&
    `${intl(
      `app.errors.form.onlyUppercaseLettersNumbersAndSpace`,
      `This field can only contain uppercase letters, numbers and space`
    )}`
  );
};

export const noSpace = () => (value: any) => {
  return value && value.includes(` `) && intl('app.errors.form.noSpace', 'This field can not contain spaces');
};

export const isNumberValidation = () => (value: any) =>
  !!value && !Number.isFinite(Number(value?.toString())) && intl('app.errors.form.onlyNumbers');

export const numberGreaterThan = (min: number, excludeMin?: boolean) => (value: any) => {
  return (
    `${value}` &&
    Number.isFinite(Number(value?.toString())) &&
    ((!excludeMin && Number(`${value}`.toString()) < min) || (excludeMin && Number(`${value}`.toString()) <= min)) &&
    `${intl('app.errors.form.greaterThan', 'This field must be higher than')} ${min}`
  );
};

export const numberEqualTo = (num: number) => (value: any) => {
  return (
    checkIfNumber(value) &&
    Number(`${value}`.toString()) !== num &&
    `${intl('app.errors.form.equalTo', 'This field must be equal to')} ${num}`
  );
};

export const numberRangeThan = (min: number, max: number) => (value: any) => {
  return (
    `${value}` &&
    Number.isFinite(Number(`${value}`.toString())) &&
    (Number(`${value}`.toString()) < min || Number(`${value}`.toString()) > max) &&
    `${intl(`app.errors.form.rangeValidate`, `The value must be between values ${min} and ${max}`, { min, max })}`
  );
};

export const validateDefaultValue = (minFunct: any, maxFunct: any) => (value: any) => {
  return (
    checkIfNumber(value) &&
    checkIfNumber(minFunct()) &&
    checkIfNumber(maxFunct()) &&
    (Number(value.toString()) < minFunct() || Number(value.toString()) > maxFunct()) &&
    `${intl(`app.errors.form.betweenMaxAndMin`, `This field must be between the max and min values`)}`
  );
};

export const numberLesserThan = (max: number, excludeMax?: boolean) => (value: any) => {
  return (
    `${value}` &&
    Number.isFinite(Number(`${value}`.toString())) &&
    ((!excludeMax && Number(`${value}`.toString()) > max) || (excludeMax && Number(`${value}`.toString()) >= max)) &&
    `${intl('app.errors.form.lesserThan', 'This field must be less than')} ${max}`
  );
};

export const dateGreaterThan = (date: Date, intl: InjectedIntl) => (value: any) => {
  return (
    value &&
    (moment(date).isAfter(moment(value)) || moment(date).isSame(moment(value))) &&
    `${intl.formatMessage({
      id: `app.errors.form.greaterThan`,
      defaultMessage: `This field must be higher than`
    })} ${moment(date, 'MM/DD/YYYY').format('MM/DD/YYYY')}`
  );
};

export const dateBeforeThan = (date: Date, intlOld?: InjectedIntl) => (value: any) => {
  return (
    value &&
    moment(value).isBefore(moment(date)) &&
    `${intl(`app.errors.form.greaterThan`, `This field must be higher than`)} ${moment(date, 'MM/DD/YYYY').format(
      'MM/DD/YYYY'
    )}`
  );
};

/**
 *
 * @param fieldState : form field to compare with value
 * @param intlOld: i18n messages (Deprecated)
 */
export const validatorDatesIsBefore = (fieldState: FieldState<any>, intlOld?: InjectedIntl) => (value: any) => {
  return (
    fieldState.value &&
    moment(value).isBefore(moment(fieldState.value)) &&
    `${intl(`app.errors.form.greaterThan`, `This field must be higher than`)} ${moment(
      fieldState.value,
      'MM/DD/YYYY'
    ).format('MM/DD/YYYY')}`
  );
};

/**
 *
 * @param fieldState : form field to compare with value
 * @param intl: i18n messages
 */
export const validatorDatesIsSameOrBefore = (fieldState: FieldState<any>, intl: InjectedIntl) => (value: any) => {
  return (
    fieldState.value &&
    moment(value).isSameOrBefore(moment(fieldState.value)) &&
    `${intl.formatMessage({
      id: `app.errors.form.greaterThan`,
      defaultMessage: `This field must be higher than`
    })} ${moment(fieldState.value, 'MM/DD/YYYY').format('MM/DD/YYYY')}`
  );
};

/**
 * This validator is for dependent fields
 * @param fieldState : form field to compare with value
 * @param intl: i18n messages
 */
export const validatorDatesIsSameOrAfterDepentField =
  (fieldState: FieldState<any>, intl: InjectedIntl) => (value: any) => {
    if (fieldState.value && moment(value).isSameOrAfter(moment(fieldState.value))) {
      fieldState.setError(
        `${intl.formatMessage({
          id: `app.errors.form.greaterThan`,
          defaultMessage: `This field must be higher than`
        })} ${moment(value, 'MM/DD/YYYY').format('MM/DD/YYYY')}`
      );
    } else {
      fieldState.setError('');
    }
  };

/**
 * This validator is for dependent fields
 * @param fieldState : form field to compare with value
 * @param intl: i18n messages
 */
export const validatorDatesIsSameOrBeforeDepentField =
  (fieldState: FieldState<any>, intl: InjectedIntl) => (value: any) => {
    if (fieldState.value && moment(value).isSameOrBefore(moment(fieldState.value))) {
      fieldState.setError(
        `${intl.formatMessage({
          id: `app.errors.form.greaterThan`,
          defaultMessage: `This field must be higher than`
        })} ${moment(value, 'MM/DD/YYYY').format('MM/DD/YYYY')}`
      );
    } else {
      fieldState.setError('');
    }
  };

export const getFtInchFrac = (value: number) => {
  if (value.toString().includes('.')) {
    const splittedNumber = value.toString().split('.');
    const number = Number(splittedNumber[0]);
    const fraction = value !== 0.0625 ? Number(splittedNumber[1]) : splittedNumber[1];
    return getProperFeetInchValues(number, fraction);
  }
  return getProperFeetInchValues(value);
};

const getProperFeetInchValues = (number: number, fraction: number | string = 0) => {
  if (number % 12 === 0) {
    return {
      getFt: number / 12,
      getInch: 0,
      getFrac: fraction ? `0.${typeof fraction === 'number' ? fraction.toString().replace('0', '') : fraction}` : '0'
    };
  }
  return {
    getFt: (number - (number % 12)) / 12,
    getInch: number % 12,
    getFrac: fraction ? `0.${fraction.toString().replace('0', '')}` : '0'
  };
};

export const onlyLetersNumbersAndSelectedChars = (intlOld?: InjectedIntl) => (value: any) => {
  const exp = /[^A-Za-z0-9\s\[\],'./]+/;
  return (
    value &&
    value.toString().match(exp) &&
    intl('app.errors.addressValidation', "Only Letters, Numbers and and [ ] ' . , / allowed")
  );
};

export const roundValue = (value: any, decimals: number) => {
  const numb = (checkIfNumber(value) && Number(value)) || 0;
  return Number(numb.toFixed(decimals));
};

export const validateAddress = (value: any, intl: InjectedIntl) => {
  const exp = /[^A-Za-z0-9\s\[\],'./]+/;
  return (
    value &&
    value.toString().match(exp) &&
    intl.formatMessage({
      id: 'app.errors.addressValidation',
      defaultMessage: "Only Letters, Numbers and and [ ] ' . , / allowed"
    })
  );
};

export const numberForIdsRequired = (value: number) => value === 0 && intl('app.errors.fieldFieldRequired');

export const checkIfNumber = (value: any) => {
  return value !== null && isNumber(Number(value)) && isFinite(Number(value));
};

export const getColumnTextStyle = (value: any, coverStyle: ICoverStyle | null) => {
  let textAlign;
  const fieldType = value.dynamicFieldType || value.type;
  switch (fieldType) {
    case 'TEXT':
    case 'STRING':
      textAlign = coverStyle?.textFields?.value || 'Center';
      break;
    case 'NUMERIC':
    case 'CALCULATION':
    case 'NUMBER':
      textAlign = coverStyle?.numericFields?.value || 'Center';
      break;
    case 'DATE':
    case 'TIME':
    case 'DATE_TIME':
      textAlign = coverStyle?.dateFields?.value || 'Center';
      break;
    default:
      textAlign = 'Center';
  }
  return { textAlign };
};

export const calcCapacityAndHeightByTheStaffListData = (data: any[]) => {
  let capacity = 0;
  let height = 0;
  data.forEach(item => {
    if (item && !item.error) {
      const to = item.startHeightInch || 0;
      const from = item.endHeightInch || 0;
      const bpi = item.barrelsPerInch || 0;
      if (to < from && bpi > 0) {
        height += from - to;
        capacity += (from - to) * bpi;
      }
    }
  });
  return { height, capacity };
};

export const existsLayoutField = (totalFields: DynamicField[], id?: number) => (value: any) => {
  const founded = totalFields.some(
    item => item?.id && item.id !== id && item?.fieldName?.trim().toLowerCase() === value?.trim().toLowerCase()
  );
  return (
    founded &&
    intl('setup.definitions.formAndField.createDynamicComponent.alreadyExistsLayoutField', '', { type: value })
  );
};

export const existsLabelName = (totalFields: RTUGlobalField[], id?: number) => (value: any) => {
  const founded = totalFields.some(
    item => item?.id && item.id !== id && item?.label?.trim().toLowerCase() === value?.trim().toLowerCase()
  );
  return (
    founded && intl('setup.definitions.formAndField.createDynamicComponent.alreadyExistsLabelName', '', { type: value })
  );
};

export const existsLayoutName = (layoutList: DynamicLayout[], id?: number) => (value: any) => {
  const founded = layoutList.some(
    item => item?.id && item.id !== id && item?.name?.trim().toLowerCase() === value?.trim().toLowerCase()
  );
  return founded && intl('setup.definitions.rtu.formLayout.alreadyExistsLayoutName', '', { type: value });
};

export const getDateAsStringOrEmpty = (date: any) => {
  if (!date?.date) {
    return '';
  }
  return moment(`${date.date} ${date.time || '00:00'}`).format('MM/DD/YYYY hh:mm a');
};

export const notEmpty = () => (value: any) => {
  return (
    value !== 'Select User Group Name' && intl('setup.definitions.rtu.formLayout.alreadyExistsLayoutName', 'Select')
  );
};

export const isUnique =
  (
    reportName: ZohoReportProfile[],
    activeProfile: ZohoReportProfile,
    userGroup: string,
    profileLevel: string,
    isEdit: boolean | undefined
  ) =>
  (value: any) => {
    if (activeProfile && isEdit) {
      if (value === activeProfile.name) return;
    }
    if (userGroup && profileLevel === 'USER GROUP') {
      if (reportName.some(report => report.userGroupName === userGroup && report.name === value)) {
        return intl('dashboard.zohoReport.uniqueValidation', 'Please Keep Zoho Report Name Unique');
      }
    } else {
      if (reportName.some(report => report.profileLevel === profileLevel && report.name === value)) {
        return intl('dashboard.zohoReport.uniqueValidation', 'Please Keep Zoho Report Name Unique');
      }
    }
  };
